// src/components/admin/GlobalContentManagement.js - Main global content management component
import React, { useState, useEffect } from 'react';
// import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useApiCall } from '../../hooks/useApiCall';
import apiService from '../../services/apiService';
import ContentList from '../global-content/ContentList';
import ContentEditor from '../global-content/ContentEditor';
import ContentStats from '../global-content/ContentStats';
import './Admin.css';
import '../global-content/GlobalContent.css';

const CONTENT_TYPES = [
  { value: 'prompt', label: 'Prompt', description: 'User-facing messages' },
  { value: 'instruction', label: 'Instruction', description: 'AI system behavior rules' },
  { value: 'context', label: 'Context', description: 'Reference materials' },
  { value: 'example', label: 'Example', description: 'Demonstration patterns' },
  { value: 'output_format', label: 'Output Format', description: 'Response structure templates' }
];


const GlobalContentManagement = () => {
  const { user } = useAuth();
  const [content, setContent] = useState([]);
  const [methodologies, setMethodologies] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedContent, setSelectedContent] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [filters, setFilters] = useState({
    contentType: 'all',
    methodology: 'real',
    search: '',
    page: 1,
    limit: 25,
    orderBy: 'updated_at',
    orderDirection: 'DESC'
  });
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    hasMore: false
  });

  const { loading, error, makeRequest } = useApiCall();

  useEffect(() => {
    loadMethodologies();
  }, []);

  const loadMethodologies = async () => {
    try {
      const data = await apiService.get('/api/v1/methodologies', user);
      if (data.success && data.data?.length) {
        setMethodologies(data.data);
        setFilters(prev => ({ ...prev, methodology: data.data[0].id }));
      }
    } catch (err) {
      console.error('Error loading methodologies:', err);
    }
  };

  useEffect(() => {
    loadContent();
    loadStats();
  }, [filters]);

  const loadContent = async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', filters.page.toString());
      params.append('limit', filters.limit.toString());
      params.append('orderBy', filters.orderBy);
      params.append('orderDirection', filters.orderDirection);
      
      if (filters.contentType && filters.contentType !== 'all') {
        params.append('contentType', filters.contentType);
      }
      if (filters.methodology) {
        params.append('methodology', filters.methodology);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }

      const data = await makeRequest(
        () => apiService.get(`/api/v1/global-content?${params}`, user),
        'Loading content...'
      );

      if (data.success) {
        const contentArray = data.data?.content || [];
        setContent(contentArray);
        setPagination(data.data?.pagination || {});
      }
    } catch (err) {
      console.error('Error loading content:', err);
    }
  };

  const loadStats = async () => {
    try {
      const data = await makeRequest(
        () => apiService.get('/api/v1/global-content/stats', user),
        'Loading statistics...'
      );

      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleCreateContent = () => {
    setSelectedContent(null);
    setShowEditor(true);
  };

  const handleEditContent = async (contentItem) => {
    try {
      // Fetch full content item if it doesn't have content field
      let fullContentItem = contentItem;
      
      if (!contentItem.content) {
        const methodology = contentItem.methodologyId || filters.methodology;
        const data = await makeRequest(
          () => apiService.get(`/api/v1/global-content/${contentItem.key}?methodology=${methodology}`, user),
          'Loading content details...'
        );
        
        if (data.success) {
          fullContentItem = data.data;
        }
      }
      
      setSelectedContent(fullContentItem);
      setShowEditor(true);
    } catch (err) {
      console.error('Error loading content details:', err);
    }
  };

  const handleSaveContent = async (contentData) => {
    try {
      let data;
      
      if (selectedContent) {
        // Update existing content — methodologyId comes from the content object (read-only)
        const putData = { ...contentData, methodologyId: selectedContent.methodologyId };
        data = await makeRequest(
          () => apiService.put(`/api/v1/global-content/${selectedContent.key}`, putData, user),
          'Updating content...'
        );
      } else {
        // Create new content — methodologyId comes from the form
        data = await makeRequest(
          () => apiService.post('/api/v1/global-content', contentData, user),
          'Creating content...'
        );
      }

      if (data.success) {
        setShowEditor(false);
        setSelectedContent(null);
        await loadContent();
        await loadStats();
        return data;
      }
    } catch (err) {
      console.error('Error saving content:', err);
      throw err;
    }
  };

  const handleArchiveContent = async (contentItem) => {
    // if (!confirm('Are you sure you want to archive this content? It can be restored later.')) {
    //   return;
    // }

    const methodology = contentItem.methodologyId || filters.methodology;
    try {
      const data = await makeRequest(
        () => apiService.delete(`/api/v1/global-content/${contentItem.key}?methodology=${methodology}`, user),
        'Archiving content...'
      );

      if (data.success) {
        await loadContent();
        await loadStats();
        return data;
      }
    } catch (err) {
      console.error('Error archiving content:', err);
      throw err;
    }
  };

  const handleFetchContent = async (contentKey, methodologyId) => {
    const methodology = methodologyId || filters.methodology;
    // Use apiService directly (not makeRequest) so the shared loading state
    // doesn't unmount ContentCards and wipe their showPreview state.
    const data = await apiService.get(`/api/v1/global-content/${contentKey}?methodology=${methodology}`, user);
    if (data.success) return data.data;
    return null;
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setSelectedContent(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getContentTypeInfo = (type) => {
    return CONTENT_TYPES.find(ct => ct.value === type) || { label: type, description: '' };
  };

  return (
    <div className="admin-page global-content-management">
      {/* Header */}
      <div className="flex flex-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Methodology Management</h2>
          <p className="text-gray-600 mt-1">
            Manage AI prompts, instructions, and content used by the selected methodology. 
          </p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => loadContent()}
            className="btn btn-secondary"
            disabled={loading}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button 
            onClick={handleCreateContent}
            className="btn btn-primary"
            disabled={loading}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Content
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="content-tabs mb-6">
        <button 
          className={`tab-button ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          Content List
        </button>
        {/* <button 
          className={`tab-button ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          Statistics
        </button> */}
          <button 
          className={`tab-button ${activeTab === 'help' ? 'active' : ''}`}
          onClick={() => setActiveTab('help')}
        >
          Help
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'list' && (
        <ContentList
          content={content}
          filters={filters}
          pagination={pagination}
          loading={loading}
          error={error}
          contentTypes={CONTENT_TYPES}
          methodologies={methodologies}
          onFilterChange={handleFilterChange}
          onPageChange={handlePageChange}
          onEditContent={handleEditContent}
          onArchiveContent={handleArchiveContent}
          onFetchContent={handleFetchContent}
          formatDate={formatDate}
          getContentTypeInfo={getContentTypeInfo}
        />
      )}

      {activeTab === 'stats' && (
        <ContentStats
          stats={stats}
          loading={loading}
          contentTypes={CONTENT_TYPES}
        />
      )}

      {/* Help tab with image */}
      {activeTab === 'help' && (
        <div className="help-content" style={{ textAlign: 'center', padding: '20px' }}>
          <img 
            src="/helpfiles/GContentHelp.png" 
            alt="Help Documentation"
            style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd' }}
          />
        </div>
      )}

      {/* Content Editor Modal */}
      {showEditor && (
        <ContentEditor
          content={selectedContent}
          contentTypes={CONTENT_TYPES}
          methodologies={methodologies}
          defaultMethodologyId={filters.methodology}
          onSave={handleSaveContent}
          onClose={handleCloseEditor}
          loading={loading}
        />
      )}
    </div>
  );
};

GlobalContentManagement.propTypes = {};

export default GlobalContentManagement;